import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, SkipForward } from 'lucide-react';
import { calculateEloRating, getRandomPair, getBestPair} from '../utils/eloUtils';
import {updateRanking } from '../utils/api'; // updated import
import { generateFingerprint } from '../utils/fingerprint';
import {getAllItems, getItems } from '../utils/api'; // updated import
import LoadingSpinner from '../utils/loadingSpinner'
import Footer from "./Footer"

const DEMO_DATA = {
  DEMO123: {
    id: 'DEMO123',
    publicId: 'DEMO123',
    title: 'Favorite Programming Languages',
    description: 'Vote for your favorite programming language',
    items: [
      { id: '1', name: 'JavaScript', elo: 1250, wins: 3, losses: 1, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRfQ4MH3vLA6l72ULn3Up_6undPBcXoERMFcA&s' },
      { id: '2', name: 'TypeScript', elo: 1220, wins: 2, losses: 1, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f5/Typescript.svg/1200px-Typescript.svg.png' },
      { id: '3', name: 'Python', elo: 1200, wins: 2, losses: 2, imageUrl: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c3/Python-logo-notext.svg/1200px-Python-logo-notext.svg.png' },
      { id: '4', name: 'React', elo: 1180, wins: 1, losses: 3, imageUrl: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQcR5U16C8yXgBpl7-Bc7Itjx3_LRl425zINA&s' }
    ],
    totalComparisons: 6,
    creatorId: 'demo-creator',
    adminCode: 'ADMIN1'
  }
};

const ComparisonView = () => {
  const { listId } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [currentPair, setCurrentPair] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparisons, setComparisons] = useState(0);
  const [isDemo, setIsDemo] = useState(false);
  const [isHandlingChoice, setIsHandlingChoice] = useState(false);
  
  // New states for animation
  const [showElo, setShowElo] = useState(false);
  const [eloChanges, setEloChanges] = useState({ winner: null, loser: null });
  const [selectedWinner, setSelectedWinner] = useState(null);
  const [previousElo, setPreviousElo] = useState({ item1: null, item2: null });

  useEffect(() => {
    const loadList = async () => {
      try {
        setLoading(true);
        // if (DEMO_DATA[listId]) {
        //   setList(DEMO_DATA[listId]);
        //   setComparisons(DEMO_DATA[listId].totalComparisons || 0);
        //   //setIsDemo(true);
        //   const pair = getBestPair(DEMO_DATA[listId].items);
        //   setCurrentPair([pair[0], pair[1]]);
        //   return;
        // }

        const fingerprint = await generateFingerprint();
        const result = await getAllItems(listId, fingerprint);
        const itemsArray = Object.entries(result.items).map(([id, data]) => ({
                  id,
                  ...data
                }));
        setList(itemsArray);
        setComparisons(result.meta.total_comparisons || 0);

        generateNewPair(itemsArray);
      } catch (err) {
        console.error('Error loading list:', err);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    loadList();
  }, [listId, navigate]);

  const generateNewPair = async (items) => {
    const pair = getBestPair(items);
    
    if (!isDemo) {

      const [rawA, rawB] = await getItems([pair[0].id, pair[1].id]);

      const freshA = {
        name : rawA[0],imageUrl: rawA[1], elo: rawA[2],wins: rawA[3],losses: rawA[4]
      }
      const freshB = {
        name : rawB[0],imageUrl: rawB[1],elo: rawB[2],wins: rawB[3],losses: rawB[4]
      }
      const firstUpdated = { ...freshA, id: pair[0].id };
      const secondUpdated = { ...freshB, id: pair[1].id };

      const updatedItems = items.map(item => {
        if (item.id === firstUpdated.id) return firstUpdated;
        if (item.id === secondUpdated.id) return secondUpdated;
        return item;
      });

      setList(updatedItems);
      
      // Only reset animation states and set new pair after everything is loaded
      setShowElo(false);
      setEloChanges({ winner: null, loser: null });
      setSelectedWinner(null);
      setCurrentPair([firstUpdated, secondUpdated]);
      
      // Store previous ELO for animation
      setPreviousElo({ 
        item1: firstUpdated.elo, 
        item2: secondUpdated.elo 
      });
    } else {
      // Reset animation states first for demo
      setShowElo(false);
      setEloChanges({ winner: null, loser: null });
      setSelectedWinner(null);
      setCurrentPair([pair[0], pair[1]]);
      setPreviousElo({ 
        item1: pair[0].rating || pair[0].elo, 
        item2: pair[1].rating || pair[1].elo 
      });
    }
  };

  const handleSkip = async () => {
    if (!list || !currentPair || isHandlingChoice) return;
    setIsHandlingChoice(true);

    try {
      // Just generate a new pair without updating any scores
      await generateNewPair(isDemo ? DEMO_DATA[listId].items : list);
    } catch (error) {
      console.error('Error skipping pair:', error);
    } finally {
      setIsHandlingChoice(false);
    }
  };

  const handleChoice = async (winner, loser) => {
    if (!list || !currentPair || isHandlingChoice) return;
    setIsHandlingChoice(true);
    setSelectedWinner(winner.id);

    try {
      if (!isDemo) {
        const result = await updateRanking(winner.id, loser.id, listId);
        if (!result || !result[0]) {
          alert("Error updating scores");
          return;
        }

        const updatedItems = Object.values(list).map(item => {
          if (item.id === winner.id) {
            return { ...item, elo: result[1], wins: result[3] };
          } else if (item.id === loser.id) {
            return { ...item, elo: result[2], losses: result[4] };
          }
          return item;
        });

        const updatedList = {
          items: updatedItems,
          totalComparisons: result[5]
        };

        setList(updatedList.items);
        setComparisons(updatedList.totalComparisons);

        // Calculate ELO changes for animation
        const winnerItem = updatedItems.find(item => item.id === winner.id);
        const loserItem = updatedItems.find(item => item.id === loser.id);
        
        const winnerPrevElo = result[6];
        const loserPrevElo =result[7];
        
        setEloChanges({
          winner: {
            change: parseFloat((winnerItem.elo - winnerPrevElo).toFixed(4)),
            newElo: winnerItem.elo,
            prevElo: winnerPrevElo
          },
          loser: {
            change: parseFloat((loserItem.elo - loserPrevElo).toFixed(4)),
            newElo: loserItem.elo,
            prevElo: loserPrevElo
          }
        });

        // Show ELO animation
        setShowElo(true);

        // Wait for animation then generate new pair
        setTimeout(async () => {
          await generateNewPair(updatedList.items);
        setIsHandlingChoice(false);
      }, 3000);

      } else {
        // Demo mode - just show animation with fake changes
        const winnerPrevElo = winner.id === currentPair[0].id ? previousElo.item1 : previousElo.item2;
        const loserPrevElo = loser.id === currentPair[0].id ? previousElo.item1 : previousElo.item2;
        
        setEloChanges({
          winner: {
            change: Math.floor(Math.random() * 20) + 10, // Random gain 10-30
            newElo: winnerPrevElo + Math.floor(Math.random() * 20) + 10,
            prevElo: winnerPrevElo
          },
          loser: {
            change: -(Math.floor(Math.random() * 20) + 10), // Random loss 10-30
            newElo: loserPrevElo - Math.floor(Math.random() * 20) - 10,
            prevElo: loserPrevElo
          }
        });

        setShowElo(true);

        setTimeout(() => {
          const pair = getBestPair(DEMO_DATA[listId].items);
          setCurrentPair([pair[0], pair[1]]);
          setShowElo(false);
          setEloChanges({ winner: null, loser: null });
          setSelectedWinner(null);
          setPreviousElo({ 
            item1: pair[0].rating || pair[0].elo, 
            item2: pair[1].rating || pair[1].elo 
          });
        }, 3000);
      }
    } catch (error) {
      console.error('Error updating comparison:', error);
    } finally {
      
    }
  };

  const getItemEloDisplay = (item, index) => {
    if (!showElo) return null;
    
    const isWinner = selectedWinner === item.id;
    const eloData = isWinner ? eloChanges.winner : eloChanges.loser;
    
    if (!eloData) return null;

    return (
      <div className="elo-animation-container">
        <div className={`elo-change ${isWinner ? 'winner' : 'loser'}`}>
          {isWinner ? '+' : ''}{eloData.change}
        </div>
        <div className="elo-transition">
          <span className="old-elo">{eloData.prevElo}</span>
          <span className="arrow">→</span>
          <span className="new-elo">{eloData.newElo}</span>
        </div>
      </div>
    );
  };

  const getItemBorderClass = (item) => {
    if (!showElo) return '';
    return selectedWinner === item.id ? 'winner-border' : 'loser-border';
  };

  if (loading || !list || !currentPair) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container">

      <button onClick={() => navigate(`/list/${listId}`)} className="back-btn">
        <ArrowLeft size={20} />
        Back to List
      </button>

      <div className="comparison-header">
        <h1>Which is better?</h1>
        <p>Comparisons made: {comparisons}</p>
      </div>

      <div className="comparison-container">
        <div
          className={`comparison-item ${getItemBorderClass(currentPair[0])} ${!showElo ? 'stats-hidden' : ''}`}
          onClick={() => !isHandlingChoice && handleChoice(currentPair[0], currentPair[1])}
          style={{
            opacity: isHandlingChoice ? 1 : 1,
            pointerEvents: isHandlingChoice ? 'none' : 'auto',
            cursor: isHandlingChoice ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          {currentPair[0].imageUrl && (
            <img src={currentPair[0].imageUrl} alt={currentPair[0].name} className="comparison-image" />
          )}
          <div className="comparison-content">
            <h2>{currentPair[0].name}</h2>
            {showElo && (
              <>
                <p>Rating: {currentPair[0].elo || currentPair[0].rating}</p>
                <p>{currentPair[0].wins}W - {currentPair[0].losses}L</p>
              </>
            )}
          </div>
          {getItemEloDisplay(currentPair[0], 0)}
        </div>

        <div className="vs-divider">VS</div>
        
        <div
          className={`comparison-item ${getItemBorderClass(currentPair[1])} ${!showElo ? 'stats-hidden' : ''}`}
          onClick={() => !isHandlingChoice && handleChoice(currentPair[1], currentPair[0])}
          style={{
            opacity: isHandlingChoice ? 1 : 1,
            pointerEvents: isHandlingChoice ? 'none' : 'auto',
            cursor: isHandlingChoice ? 'not-allowed' : 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          {currentPair[1].imageUrl && (
            <img src={currentPair[1].imageUrl} alt={currentPair[1].name} className="comparison-image" />
          )}
          <div className="comparison-content">
            <h2>{currentPair[1].name}</h2>
            {showElo && (
              <>
                <p>Rating: {currentPair[1].elo || currentPair[1].rating}</p>
                <p>{currentPair[1].wins}W - {currentPair[1].losses}L</p>
              </>
            )}
          </div>
          {getItemEloDisplay(currentPair[1], 1)}
        </div>
      </div>

      {/* Skip Button */}
      <div className="skip-container">
        <button 
          onClick={handleSkip}
          disabled={isHandlingChoice}
          className="skip-btn"
        >
          <SkipForward size={20} />
          Skip this comparison
        </button>
      </div>

      {isDemo && (
        <div className="demo-notice">
          <p>This is a demo - changes won't be saved</p>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ComparisonView;